import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { UserCheck, UserX, Trash2, Clock, Send, Building2, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

const OWNER = "courtresearcher@yourdelawarepi.com";

export function UserAccess({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [inv, setInv] = useState({ email: "", name: "", role: "server" });
  const [inviting, setInviting] = useState(false);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const sendInvite = async (e) => {
    e.preventDefault(); setInviting(true);
    try { await api.post("/users/invite", inv); toast.success(`Invite sent to ${inv.email} — they're pre-approved`); setInv({ email: "", name: "", role: "server" }); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed to send invite"); }
    finally { setInviting(false); }
  };

  const patch = async (u, body, msg) => {
    try { await api.patch(`/users/${u.user_id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (u) => {
    if (!window.confirm(u.approved ? `Remove ${u.email}? They will need to request access again.` : `Deny ${u.email}? They will be emailed that Simply Served is private and removed from this list.`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const pending = users.filter((u) => !u.approved);
  const approved = users.filter((u) => u.approved);

  const Row = ({ u, children }) => (
    <div className="flex items-center gap-3 py-2.5 text-sm" data-testid={`user-row-${u.user_id}`}>
      <Avatar className="h-8 w-8"><AvatarImage src={u.picture} /><AvatarFallback className="text-xs">{(u.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{u.name}</div>
        <div className="text-xs text-slate-500 truncate">{u.email}{u.email === OWNER ? " · owner" : ""}{u.invited_by ? " · invited" : ""}{u.org_name ? ` · ${u.org_name}` : u.requested_org_name ? ` · requesting ${u.requested_org_name}` : ""}</div>
        {!u.approved && u.request_company && (
          <div className="text-xs text-slate-700 mt-0.5" data-testid={`pending-company-${u.user_id}`}>
            <Building2 className="inline h-3 w-3 mr-1 text-slate-400" />Company: <strong>{u.request_company}</strong>
            {u.request_note && <span className="text-slate-500 italic"> — “{u.request_note}”</span>}
          </div>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div data-testid="user-access-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="sm-label">Access control</div>
          <h2 className="text-lg font-semibold">Who can use Simply Served</h2>
          <p className="text-sm text-slate-500">Anyone can sign in with Google, but only accounts you approve here can see or do anything. Approving adds them to your organization. Admins only receive notification emails (new jobs, website leads, access requests, job updates) when you turn <b>Emails on</b> for them.</p>
        </div>
      </div>

      <div className="mt-5 rounded border border-slate-200 bg-slate-50 p-4" data-testid="invite-card">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Send className="h-4 w-4 text-[#2d7bd0]" />Invite by email</div>
        <p className="text-xs text-slate-500 mt-0.5">They get a one-click sign-in link, already approved and in your organization. Works with any email provider.</p>
        <form className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px_auto] gap-2" onSubmit={sendInvite}>
          <Input data-testid="invite-email" type="email" required placeholder="server@yahoo.com" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} className="h-9 bg-white" />
          <Input data-testid="invite-name" placeholder="Name (optional)" value={inv.name} onChange={(e) => setInv({ ...inv, name: e.target.value })} className="h-9 bg-white" />
          <select data-testid="invite-role" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })}>
            <option value="server">Server</option><option value="admin">Admin</option><option value="client">Client</option>
          </select>
          <Button type="submit" size="sm" className="sm-green h-9" disabled={inviting || !inv.email} data-testid="invite-submit">{inviting ? "Sending…" : "Send invite"}</Button>
        </form>
      </div>

      <div className="mt-5">
        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800"><Clock className="h-4 w-4" />Awaiting approval <span className="text-xs font-normal text-slate-500" data-testid="pending-count">({pending.length})</span></div>
        <div className="divide-y divide-slate-100 border-t border-slate-200 mt-2">
          {pending.length === 0 ? <div className="py-3 text-sm text-slate-400">No pending requests.</div> : pending.map((u) => (
            <Row key={u.user_id} u={u}>
              <Select defaultValue="server" onValueChange={(v) => (u._role = v)}>
                <SelectTrigger className="h-8 w-28 text-xs" data-testid={`pending-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="server">Server</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
              <Button size="sm" className="sm-green h-8" data-testid={`approve-user-${u.user_id}`} onClick={() => patch(u, { approved: true, role: u._role || "server" }, `${u.name} approved`)}><UserCheck className="h-3.5 w-3.5 mr-1" />Approve</Button>
              <Button size="sm" variant="ghost" className="h-8 text-rose-600" data-testid={`deny-user-${u.user_id}`} onClick={() => remove(u)}><UserX className="h-3.5 w-3.5 mr-1" />Deny</Button>
            </Row>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <div className="text-sm font-semibold text-slate-800">Approved users <span className="text-xs font-normal text-slate-500" data-testid="approved-count">({approved.length})</span></div>
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-md mt-2 max-h-72 overflow-y-auto" data-testid="approved-users-list">
          {approved.map((u) => {
            const isOwner = u.email === OWNER;
            return (
              <Row key={u.user_id} u={u}>
                <Select value={u.role} disabled={isOwner} onValueChange={(v) => patch(u, { role: v }, "Role updated")}>
                  <SelectTrigger className="h-8 w-28 text-xs" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="server">Server</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
                {u.role === "admin" && (
                  <button type="button" disabled={isOwner} title={isOwner ? "The owner always receives notification emails" : (u.notify_emails ? "Receives notification emails (new jobs, leads, access requests, job updates). Click to turn off." : "Not receiving notification emails. Click to turn on.")}
                    className={`h-8 px-2 rounded-md border text-xs inline-flex items-center gap-1 transition-colors ${u.notify_emails || isOwner ? "border-[#2d7bd0]/40 bg-[#eef5fc] text-[#1f5fa8]" : "border-slate-200 bg-white text-slate-400 hover:text-slate-600"} disabled:opacity-70`}
                    onClick={() => patch(u, { notify_emails: !u.notify_emails }, u.notify_emails ? "Notification emails off" : "Notification emails on")} data-testid={`notify-toggle-${u.user_id}`} aria-pressed={!!(u.notify_emails || isOwner)}>
                    {u.notify_emails || isOwner ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}{u.notify_emails || isOwner ? "Emails on" : "Emails off"}
                  </button>
                )}
                {!isOwner && u.user_id !== currentUser?.user_id && (
                  <>
                    <Button size="sm" variant="outline" className="h-8" data-testid={`revoke-user-${u.user_id}`} onClick={() => patch(u, { approved: false }, "Access revoked")}>Revoke</Button>
                    <Button size="sm" variant="ghost" className="h-8 text-slate-400 hover:text-rose-600" data-testid={`delete-user-${u.user_id}`} onClick={() => remove(u)}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </Row>
            );
          })}
        </div>
      </div>
    </div>
  );
}
